// v-dec-12-2024

/**
 * Add commas to a number string
 * @param {string} numStr - The number string without commas
 * @param {string} decimalPart - The decimal part of the number (if any)
 * @returns {string} - The number string with commas added
 */
function addCommas(numStr, decimalPart = "") {
  numStr = numStr.replace(/,/g, "");
  if (numStr.length > 9) {
    return `${numStr.slice(0, -9)},${numStr.slice(-9, -6)},${numStr.slice(
      -6,
      -3
    )},${numStr.slice(-3)}${decimalPart}`;
  }
  if (numStr.length > 6) {
    return `${numStr.slice(0, -6)},${numStr.slice(-6, -3)},${numStr.slice(
      -3
    )}${decimalPart}`;
  }
  if (numStr.length > 3) {
    return `${numStr.slice(0, -3)},${numStr.slice(-3)}${decimalPart}`;
  }
  return `${numStr}${decimalPart}`;
}

/**
 * Generate all valid variations of a number string
 * @param {string|number} inputStr - The input number string
 * @param {Object} options - Options for generating variations
 * @returns {string[]} - Array of number variations
 */
function generateNumberVariations(inputStr, options = {}) {
  inputStr = String(inputStr);

  if (
    inputStr.length === 0 ||
    inputStr === null ||
    inputStr === undefined ||
    inputStr === "null" ||
    inputStr === "undefined" ||
    inputStr === "NaN" ||
    inputStr === "None" ||
    inputStr === "none"
  ) {
    return [""];
  }

  if (inputStr.endsWith(".0")) {
    inputStr = inputStr.slice(0, -2);
  }
  if (inputStr.endsWith(".00")) {
    inputStr = inputStr.slice(0, -3);
  }

  let variations = [];

  const {
    includeTrailingZeroCombination = true,
    includePrefixCombination = true,
    includeSuffixCombination = true,
    includeDecimalCombination = true,
    includeCommaCombination = true,
    includeSpaceBetweenDecimalCombination = false,
  } = options;

  try {
    const match = inputStr.match(
      /^([$+\-\(\) ]*)\s*([0-9,.]+)([%\s\(\)a-zA-Z]*)$/
    );
    if (!match) {
      return [inputStr];
    }

    let [, prefixStr, num, suffixStr] = match;
    const numTrimmed = num.trim();

    const prefixCombos = includePrefixCombination
      ? generateCombinations(prefixStr)
      : [prefixStr];
    const suffixCombos = includeSuffixCombination
      ? generateCombinations(suffixStr)
      : [suffixStr];

    let numVariations = [numTrimmed];

    if (includeDecimalCombination) {
      if (numTrimmed.includes(".")) {
        const [intPart, decimalPart] = numTrimmed.split(".");
        if (decimalPart.endsWith("0")) {
          const withoutTrailingZeros = numTrimmed.replace(/\.?0+$/, "");
          numVariations.push(withoutTrailingZeros);
        } else if (decimalPart.length === 1) {
          numVariations.push(numTrimmed + "0");
        }
      } else {
        numVariations.push(numTrimmed + ".0", numTrimmed + ".00");
      }
    }

    if (includeTrailingZeroCombination && numTrimmed.endsWith("000")) {
      let withoutTrailingZeros = numTrimmed.slice(0, -3);
      if (withoutTrailingZeros.endsWith(",")) {
        withoutTrailingZeros = withoutTrailingZeros.slice(0, -1);
      }
      const shortVariations = generateNumberVariations(withoutTrailingZeros);
      numVariations.push(...shortVariations);
    }

    if (includeCommaCombination) {
      if (numTrimmed.includes(",")) {
        numVariations = numVariations.flatMap((v) => [v, v.replace(/,/g, "")]);
      }
      if (!numTrimmed.includes(",")) {
        const newVariations = numVariations.map((num) => {
          if (num.includes(".")) {
            const [numBeforeDecimal, numAfterDecimal] = num.split(".");
            return addCommas(numBeforeDecimal, `.${numAfterDecimal}`);
          } else {
            return addCommas(num);
          }
        });
        numVariations.push(...newVariations);
      }
    }

    variations = prefixCombos.flatMap((p) =>
      suffixCombos.flatMap((s) => numVariations.map((n) => p + n + s))
    );

    if (includeSpaceBetweenDecimalCombination) {
      const newVariations = [];
      for (const variation of variations) {
        if (variation.includes(".")) {
          newVariations.push(variation.replace(".", ". "));
          newVariations.push(variation.replace(".", " ."));
          newVariations.push(variation.replace(".", " . "));
        }
      }
      variations.push(...newVariations);
    }

    let newVariations = [];
    for (const variation of variations) {
      if (variation.includes(".00")) {
        newVariations.push(variation.replace(".00", ".0"));
      }
      if (variation.includes(". 00")) {
        newVariations.push(variation.replace(". 00", ". 0"));
      }
    }
    variations.push(...newVariations);

    variations.sort((a, b) => b.length - a.length);
    variations.unshift(inputStr);
    variations = variations.map((v) => v.trim());
    variations = [...new Set(variations)];
  } catch (error) {
    console.error(error);
    return [inputStr];
  }

  return variations;
}

/**
 * Generate all combinations of a string
 * @param {string} string - The input string
 * @returns {string[]} - Array of string combinations
 */
function generateCombinations(string) {
  const elements = string.match(/\s*(thousand|million|billion|\S)\s*/gi) || [];
  let result = [""];

  for (const element of elements) {
    const lenResult = result.length;
    for (let j = 0; j < lenResult; j++) {
      result.push(result[j] + element);
      result.push(result[j] + " " + element);
      result.push(result[j] + element + " ");
    }
  }

  return [...new Set(result)];
}

function testNumberVariations() {
  const testCases = [
    {
      input: "$1,000.00",
      expected: [
        "$1,000.00",
        "$1,000",
        "$ 1,000.00",
        "$ 1,000",
        "$1000.00",
        "$1000",
        "$ 1000.00",
        "$ 1000",
        "1,000.00",
        "1,000",
        "1000.00",
        "1000",
      ],
    },
    {
      input: "3.6%",
      expected: ["3.60 %", "3.60%", "3.6 %", "3.6%", "3.60", "3.6"],
    },
    {
      input: "-13.46%",
      expected: [
        "-13.46 %",
        "-13.46%",
        "- 13.46 %",
        "- 13.46%",
        "-13.46",
        "- 13.46",
        "13.46 %",
        "13.46%",
        "13.46",
      ],
    },
    {
      input: "$12,045,999",
      expected: [
        "$12,045,999.00",
        "$12,045,999.0",
        "$12,045,999",
        "$ 12,045,999.00",
        "$ 12,045,999.0",
        "$ 12,045,999",
        "$12045999.00",
        "$12045999.0",
        "$12045999",
        "$ 12045999.00",
        "$ 12045999.0",
        "$ 12045999",
        "12,045,999.00",
        "12,045,999.0",
        "12,045,999",
        "12045999.00",
        "12045999.0",
        "12045999",
      ],
    },
    {
      input: "$12,045,999.00",
      expected: [
        "$12,045,999.00",
        "$ 12,045,999.00",
        "$12,045,999",
        "$ 12,045,999",
        "$12045999.00",
        "$ 12045999.00",
        "$12045999",
        "$ 12045999",
        "12,045,999.00",
        "12,045,999",
        "12045999.00",
        "12045999",
      ],
    },
    {
      input: "1.70%",
      expected: ["1.70 %", "1.70%", "1.7 %", "1.7%", "1.70", "1.7"],
    },
    {
      input: "+42",
      expected: [
        "+42.00",
        "+42.0",
        "+42",
        "+ 42.00",
        "+ 42.0",
        "+ 42",
        "42.00",
        "42.0",
        "42",
      ],
    },
    {
      input: "1,234,567.890",
      expected: ["1,234,567.890", "1234567.890", "1,234,567.89", "1234567.89"],
    },
    {
      input: "3.4",
      expected: ["3.40", "3.4"],
    },
    {
      input: "3.40",
      expected: ["3.40", "3.4"],
    },
    {
      input: "322",
      expected: ["322.00", "322.0", "322"],
    },
    {
      input: "$-22.55",
      expected: [
        "$-22.55",
        "$ -22.55",
        "$- 22.55",
        "$  -22.55",
        "$ - 22.55",
        "$22.55",
        "$ 22.55",
        "-22.55",
        "- 22.55",
        "22.55",
      ],
    },
    {
      input: "3,804,000",
      expected: [
        "3,804,000.00",
        "3,804,000.0",
        "3,804,000",
        "3804000.00",
        "3804000.0",
        "3804000",
        "3,804.00",
        "3,804.0",
        "3,804",
        "3804.00",
        "3804.0",
        "3804",
      ],
    },
    {
      input: "3804000",
      expected: [
        "3,804,000.00",
        "3,804,000.0",
        "3,804,000",
        "3804000.00",
        "3804000.0",
        "3804000",
        "3,804.00",
        "3,804.0",
        "3,804",
        "3804.00",
        "3804.0",
        "3804",
      ],
    },
  ];

  for (const testCase of testCases) {
    const inputStr = testCase.input;
    const expected = testCase.expected;
    const result = generateNumberVariations(inputStr);
    const missingExpected = expected.filter((item) => !result.includes(item));
    const unexpectedPresent = result.filter((item) => !expected.includes(item));

    console.log("\n\n----------------------------------\n");
    if (missingExpected.length === 0 && unexpectedPresent.length === 0) {
      console.log(`Test passed for input: ${inputStr} ✅`);
      continue;
    } else {
      console.log(`Test failed for input: ${inputStr} ❌`);
    }

    console.log(`Test case: ${inputStr}`);
    console.log("Function output:\n", result);
    console.log("Expected output:\n", expected);

    if (missingExpected.length > 0) {
      console.log("Missing expected variations:\n", missingExpected);
    }
    if (unexpectedPresent.length > 0) {
      console.log("Unexpected present variations:\n", unexpectedPresent);
    }
    if (missingExpected.length === 0 && unexpectedPresent.length === 0) {
      console.log("All variations match expected output.");
    }
  }
}

export default generateNumberVariations;
